import React, { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import CategoryProgress from "./CategoryProgress";
import { useSalesContext } from "../../SalesContext";
import { API_BASE_URL } from "../../../config/config";
interface Category {
  name: string;
  current: number;
  total: number;
  percentage: number;
  color: string;
}

interface DoctorStatsProps {
  compliance?: string;
  associated?: string;
  missedDoctor?: string;
  missedCalls?: string;
  isLimited: boolean;
  categories: Category[];
  speciality: Category[];
  frequency: Category[];
}

const DoctorStats: React.FC<DoctorStatsProps> = ({
  compliance,
  associated,
  missedDoctor,
  missedCalls,
  isLimited,
  categories,
  speciality,
  frequency,
}) => {
  const [activeView, setActiveView] = useState<
    "category" | "speciality" | "frequency"
  >("category");
  const { newFilterdata, setShowtable, setKpiData, setLoading } =
    useSalesContext();
  const handleMissedDoctorsClick = async () => {
    setLoading(true);
    try {
      setShowtable(true);
      const response = await fetch(
        `${API_BASE_URL}MyPerformance/MissedDoctorsPartyDetails`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(newFilterdata),
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch Missed Doctors details");
      }
      const data = await response.json();
      setLoading(false);
      setKpiData({ Tabledata: data, isMissedDoctor: true, missedDoctor });
    } catch (error) {
      console.error("Error fetching Missed Doctors details:", error);
      setLoading(false);
    }
  };

  const handleMissedCallClick = async () => {
    try {
      setLoading(true);
      setShowtable(true);
      const response = await fetch(
        `${API_BASE_URL}MyPerformance/MissedCallsPartyDetails`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(newFilterdata),
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch Missed Doctors details");
      }
      const data = await response.json();
      setLoading(false);
      setKpiData({ Tabledata: data, isMissedCall: true, missedCalls });
    } catch (error) {
      console.error("Error fetching Missed Doctors details:", error);
      setLoading(false);
    }
  };

  return (
    <Box sx={{}}>
      <Typography
        variant="body1"
        sx={{
          fontSize: "24px",
          fontWeight: "bold",
          margin: "16px 0",
          minWidth: "50px",
        }}
      >
        {!isLimited ? "Compliance" : "Missed Doctors"}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mt: 2,
          marginBottom: "14px",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "16px",
        }}
      >
        {!isLimited ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <Typography
              variant="body1"
              sx={{
                backgroundColor: "#E5F3FF",
                borderRadius: "18px",
                p: "13.8px 1px 15.2px 1px",
                width: "96px",
                color: "#00008B",
                textAlign: "center",
                border: "1px solid #CACACA",
              }}
            >
              <strong>{compliance}</strong>/{associated}
            </Typography>
            <Typography variant="body2" color="#000">
              Doctors Covered (Compliance Doctor/Associated)
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "1rem" }}>
              <Typography
                variant="body1"
                sx={{
                  backgroundColor: "#F9E9E0",
                  borderRadius: "18px",
                  p: "13.8px 1px 15.2px 1px",
                  width: "96px",
                  color: "#F00",
                  textAlign: "center",
                  border: "1px solid #CACACA",
                  cursor: "pointer", 
                }}
                onClick={handleMissedDoctorsClick}
              >
                <strong>{missedDoctor}</strong>
              </Typography>
              <Typography variant="body2" color="#000">
                Missed Doctors (Untouched Doctors)
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "1rem" }}>
              <Typography
                variant="body1"
                sx={{
                  backgroundColor: "#F9E9E0",
                  borderRadius: "18px",
                  p: "13.8px 1px 15.2px 1px",
                  width: "96px",
                  color: "#F00",
                  textAlign: "center",
                  border: "1px solid #CACACA",
                  cursor: "pointer",
                }}
                onClick={handleMissedCallClick}
              >
                <strong>{missedCalls}</strong>
              </Typography>
              <Typography variant="body2" color="#000">
                Missed Calls
              </Typography>
            </Box>
          </Box>
        )}

        {/* Buttons Section */}
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Button
            variant={activeView === "category" ? "contained" : "outlined"}
            onClick={() => setActiveView("category")}
            sx={{
              borderRadius: "80px",
              textTransform: "none",
              backgroundColor: activeView === "category" ? "#00008B" : "#CCC",
              color: "#fff",
              borderColor: "transparent",
            }}
          >
            Category
          </Button>
          <Button
            variant={activeView === "speciality" ? "contained" : "outlined"}
            onClick={() => setActiveView("speciality")}
            sx={{
              borderRadius: "80px",
              textTransform: "none",
              backgroundColor: activeView === "speciality" ? "#00008B" : "#CCC",
              color: "#fff",
              borderColor: "transparent",
            }}
          >
            Speciality
          </Button>
          <Button
            variant={activeView === "frequency" ? "contained" : "outlined"}
            onClick={() => setActiveView("frequency")}
            sx={{
              borderRadius: "80px",
              textTransform: "none",
              backgroundColor: activeView === "frequency" ? "#00008B" : "#CCC",
              color: "#fff",
              borderColor: "transparent",
            }}
          >
            Frequency
          </Button>
        </Box>
      </Box>

      {activeView === "category" && (
        <CategoryProgress
          categories={categories}
          isLimited={isLimited}
          viewType="category"
        />
      )}
      {activeView === "speciality" && (
        <CategoryProgress
          categories={speciality}
          isLimited={isLimited}
          viewType="speciality"
        />
      )}
      {activeView === "frequency" && (
        <CategoryProgress
          categories={frequency}
          isLimited={isLimited}
          viewType="frequency"
        />
      )}
    </Box>
  );
};

export default DoctorStats;
